import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import Group from 'components/utils/Group'
import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { usePlacesBySearchPhrase } from 'hooks/usePlace'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { TRIP_PLACE_TYPEAHEAD_TYPES } from 'tripPlanner/config/search'
import { useTrendingDestinations } from 'tripPlanner/pages/trip/useTripLandingPageTrendingDestinations'
import { destinations } from 'tripPlanner/pages/trip/TripLandingPageTrendingDestinations'
import ImageTileCard from 'tripPlanner/components/EntryPoints/Common/Tile/ImageTileCard'
import type { TrendingDestination } from 'home/components/TrendingDestinations/constants'
import TileCardsGroup from './Common/Tile/TileCardsGroup'
import { take } from 'lib/array/arrayUtils'
import Sheet from 'components/utils/Sheet'
import type { AITripDestinationListItemClickHandler } from './AITripDestinationListItem'
import AITripDestinationListItem from './AITripDestinationListItem'
import * as Analytics from 'analytics/analytics'
import type { Locations, OccupancyOption } from 'tripPlanner/pages/trip/types'
import InputChip from 'LuxKit/Chips/InputChip'
import { rem } from 'polished'
import styled from 'styled-components'
import type { SuggestedLocationItem } from 'tripPlanner/hooks/api/suggestedLocations'
import { useSuggestedLocations } from 'tripPlanner/hooks/api/suggestedLocations'
import LoadingImageTileCard from './Common/Tile/LoadingImageTileCard'

const InputChipEmptySpace = styled.div`
min-height: ${rem(28)};
`

interface Props {
  location?: string
  onLocationSelect?: (location: string) => void
  onNext?: () => void
  isGeneratingTrip?: boolean
  stepNumber?: number
  totalSteps?: number
  onAddLocation?: (params: {
    locationName: string;
    startDate?: Date;
    endDate?: Date;
    occupancy?: OccupancyOption;
    duration?: number;
    destinationId?: string
  }) => void
  locations?: Array<Locations>
  onRemoveLocation?: (locationName: string) => void
}

function AITripDestinationSelectionV2(props: Props) {
  const { onLocationSelect, stepNumber, totalSteps, onAddLocation, locations, onRemoveLocation } = props
  const intl = useIntl()
  const [searchPhrase, setSearchPhrase] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const [searchResults, isLoading, hasError] = usePlacesBySearchPhrase(searchPhrase, {
    placeTypes: TRIP_PLACE_TYPEAHEAD_TYPES,
    limit: 10,
  })

  const { finalDestinations } = useTrendingDestinations(destinations)

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'trip_destination',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchPhrase(event.currentTarget.value)
  }, [])

  const handleFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  const handleDropdownClose = useCallback(() => {
    setIsFocused(false)
  }, [])

  const handleDestinationSelect = useCallback<AITripDestinationListItemClickHandler>((selectedDestination) => {
    setIsFocused(false)
    const fullLocation = selectedDestination.format.secondaryText
      ? intl.formatMessage(
          {
            id: 'YiGZVk5NC1',
            defaultMessage: '{mainText}, {secondaryText}',
            description: 'AI trip destination selection V2 full location with city and country/region',
          },
          { mainText: selectedDestination.format.mainText, secondaryText: selectedDestination.format.secondaryText },
        )
      : selectedDestination.format.mainText
    const shortLocation = selectedDestination.format.mainText
    const destinationId = selectedDestination.value
    onLocationSelect?.(fullLocation)
    onAddLocation?.({
      locationName: shortLocation,
      destinationId: destinationId,
    })
    setSearchPhrase('')
  }, [onLocationSelect, onAddLocation, intl])

  const handleClear = useCallback(() => {
    setSearchPhrase('')
  }, [])

  const canShowDropdown = isFocused && searchPhrase.length >= 2 && !hasError
  const hasResults = searchResults.length > 0

  const handleTrendingDestinationClick = useCallback((destination: TrendingDestination) => {
    setSearchPhrase('')
    setIsFocused(false)
    onLocationSelect?.(destination.name)
    onAddLocation?.({
      locationName: destination.name,
      destinationId: destination.destinationId,
    })
  }, [onLocationSelect, onAddLocation])

  const handleRemoveLocationClick = useCallback((event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    const locationName = event.currentTarget.dataset.locationName
    if (locationName) {
      onRemoveLocation?.(locationName)
    }
  }, [onRemoveLocation])

  const handleSuggestedLocationClick = useCallback((suggestedLocation: SuggestedLocationItem) => {
    setSearchPhrase('')
    setIsFocused(false)
    onLocationSelect?.(suggestedLocation.name)
    onAddLocation?.({
      locationName: suggestedLocation.name,
      destinationId: suggestedLocation.destinationId,
    })
  }, [onLocationSelect, onAddLocation])
  const selectedLocationNames = useMemo(() => locations?.map(location => location.locationName) || [], [locations])
  const { data: suggestedLocations, isLoading: isLoadingSuggestedLocations } = useSuggestedLocations({ locations: selectedLocationNames })

  useEffect(() => {
    if (!isLoadingSuggestedLocations && suggestedLocations && suggestedLocations.length > 0) {
      suggestedLocations.forEach((location) => {
        const hasImage = !!(location.imageId || location.url)
        Analytics.trackClientEvent({
          subject: hasImage ? 'has_suggested_destination_image' : 'no_suggested_destination_image',
          action: location.destinationId || location.name,
          category: 'trip_planner',
          type: 'nonInteraction',
        })
      })
    }
  }, [suggestedLocations, isLoadingSuggestedLocations])

  const stepText = intl.formatMessage(
    {
      id: 'ewX/UMLdx4',
      defaultMessage: '{stepNumber} of {totalSteps}',
      description: 'Step indicator showing current step number and total steps',
    },
    { stepNumber: stepNumber || 1, totalSteps: totalSteps || 10 },
  )

  const headingText = intl.formatMessage(
    {
      id: 'HYUPE3oTSf',
      defaultMessage: 'Trip <i>Destinations</i>',
      description: 'Heading text with italicized Destinations',
    },
    {
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  const descriptionText = intl.formatMessage({
    id: 'NYSmRgES+v',
    defaultMessage: 'Select one or more destinations for your trip.',
    description: 'Description text encouraging users to select destinations',
  })

  const searchPlaceholder = intl.formatMessage({
    id: 'kVW27LGhr3',
    defaultMessage: 'Search by destination',
    description: 'Placeholder text for destination search input',
  })

  const whereLabel = intl.formatMessage({
    id: 'NZYFWKz38y',
    defaultMessage: 'Where',
    description: 'Label for destination search input',
  })

  const noDestinationsFound = intl.formatMessage(
    {
      id: 'V1GaBFtMaK',
      defaultMessage: 'No destinations found for "{searchPhrase}"',
      description: 'Message when no destinations found for search',
    },
    { searchPhrase },
  )

  const tryDifferentSearch = intl.formatMessage({
    id: 'XIaqFrQfOm',
    defaultMessage: 'Try a different search term',
    description: 'Suggestion to try different search term',
  })

  const suggestedDestinationsLabel = intl.formatMessage({
    id: 'gXm21zCEMu',
    defaultMessage: 'Suggested destinations for your trip',
    description: 'Label for suggested destinations section',
  })

  const noSuggestedDestinations = intl.formatMessage({
    id: 'QSp8t+K3kG',
    defaultMessage: 'No suggested destinations available',
    description: 'Message when no suggested destinations available',
  })

  const exploreTrendingLabel = intl.formatMessage({
    id: 'b1vV2T5JB0',
    defaultMessage: 'Or explore trending destinations',
    description: 'Label for trending destinations section',
  })

  return (
    <Group direction="vertical" gap={40}>
      <Group direction="vertical" gap={12}>
        <BodyText variant="large" weight="bold">{stepText}</BodyText>
        <Heading variant="heading1">
          {headingText}
        </Heading>
        <BodyText variant="large">
          {descriptionText}
        </BodyText>
        <SearchFormTextField
          placeholder={searchPlaceholder}
          value={searchPhrase}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onClear={handleClear}
          ref={inputRef}
          label={whereLabel}
        />

        <DropdownList
          size="fill-anchor"
          open={canShowDropdown}
          anchorRef={inputRef}
          triggerRef={inputRef}
          onClose={handleDropdownClose}
        >
          {isLoading && <LoadingIndicator inline />}

          {!isLoading && hasResults && searchResults.map((result) => (
            <AITripDestinationListItem
              key={result.value}
              item={result}
              onClick={handleDestinationSelect}

            />
          ))}

          {!isLoading && !hasResults && searchPhrase.length >= 2 && (
            <Sheet padding={16}>
              <BodyText variant="medium" colour="neutral-two">
                {noDestinationsFound}
              </BodyText>
              <BodyText variant="medium" colour="neutral-two">
                {tryDifferentSearch}
              </BodyText>
            </Sheet>
          )}
        </DropdownList>
        {locations && locations.length > 0 && (
          <Group direction="horizontal" gap={12} wrap="wrap">
            {locations.map((locationItem: Locations) => (
              <InputChip
                key={locationItem.locationName}
                variant="outline"
                onClick={handleRemoveLocationClick}
                data-location-name={locationItem.locationName}
              >
                {locationItem.locationName}
              </InputChip>
            ))}
          </Group>
        )}
        {!locations || locations.length === 0 && (
          <InputChipEmptySpace />
        )}
      </Group>

      <Group direction="vertical" gap={16}>
        {selectedLocationNames.length > 0 && (
          <>
            <BodyText variant="medium" weight="bold">{suggestedDestinationsLabel}</BodyText>
            <TileCardsGroup>
              {isLoadingSuggestedLocations &&
                <>
                  <LoadingImageTileCard />
                  <LoadingImageTileCard />
                  <LoadingImageTileCard />
                  <LoadingImageTileCard />
                  <LoadingImageTileCard />
                  <LoadingImageTileCard />
                </>}
              {!isLoadingSuggestedLocations && suggestedLocations && suggestedLocations.length > 0 && suggestedLocations.map((suggestedLocation) => (
                <ImageTileCard
                  key={suggestedLocation.destinationId || suggestedLocation.name}
                  title={suggestedLocation.name}
                  imageId={suggestedLocation.imageId}
                  value={suggestedLocation}
                  onClick={handleSuggestedLocationClick}
                  layout="auto"
                />
              ))}
              {!isLoadingSuggestedLocations && (!suggestedLocations || suggestedLocations.length === 0) && (
                <BodyText variant="medium" colour="neutral-two">
                  {noSuggestedDestinations}
                </BodyText>
              )}
            </TileCardsGroup>
          </>
        )}
        {selectedLocationNames.length === 0 && (
          <>
            <BodyText variant="medium" weight="bold">{exploreTrendingLabel}</BodyText>
            <TileCardsGroup>
              {take(finalDestinations, 6).map((trendingDestination) => (
                <ImageTileCard
                  key={trendingDestination.destinationId}
                  title={trendingDestination.name}
                  imageId={trendingDestination.imageId}
                  value={trendingDestination}
                  onClick={handleTrendingDestinationClick}
                  layout="auto"
                />
              ))}
            </TileCardsGroup>
          </>
        )}
      </Group>
    </Group>
  )
}

export default AITripDestinationSelectionV2
